import { Button } from '../basic';
import React, { useId } from 'react';
import type { EmojiReactionsProps } from './EmojiReactions';
import { EmojiReactions } from './EmojiReactions';
import { intl } from '../../lib';
import { FocusTrap } from 'focus-trap-react';
import type { ISendEventResponse } from 'matrix-js-sdk';
import type { LayerProps } from 'react-laag';

export type EmojiReactionsButtonProps = EmojiReactionsProps & {
  showReactionPicker: boolean;
  setShowReactionPicker: (show: boolean) => void;
  showReactionPickerTrigger: boolean;
  setShowReactionPickerTrigger: (show: boolean) => void;
  layerProps: LayerProps;
  renderLayer: (layer: React.ReactNode) => React.ReactNode;
  showTrigger: boolean;
  /**
   * Whether the picker should trap the focus. This is only the case when the
   * picker was opened explicitly by the user - on mobile the reaction bar is
   * opened as soon as a message gets focused and must not steal the focus.
   */
  trapFocus?: boolean;
  /** Ref to the emoji button, used for arrow-key focus management. */
  triggerRef?: React.Ref<HTMLButtonElement>;
};

export function EmojiReactionsButton({
  emojiData,
  reactionClick,
  eventId,
  roomId,
  showReactionPicker,
  setShowReactionPicker,
  showReactionPickerTrigger,
  setShowReactionPickerTrigger,
  renderLayer,
  layerProps,
  showTrigger,
  trapFocus = true,
  triggerRef
}: EmojiReactionsButtonProps) {
  const classNames = ['ehs-emoji-reactions-button'];
  if (showReactionPicker) {
    classNames.push('open');
  } else {
    classNames.filter(name => name !== 'open');
  }
  const menuId = useId();

  const handleReactionClick = (
    eventId: string,
    reactionKey: string,
    roomId?: string | undefined
  ): Promise<ISendEventResponse | undefined> => {
    setShowReactionPicker(false);
    setShowReactionPickerTrigger(false);
    return reactionClick(eventId, reactionKey, roomId);
  };
  const layer = renderLayer(
    // determine how to render the layer

    <FocusTrap
      active={trapFocus}
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: () => {
          setShowReactionPicker(false);
          return true;
        },
        preventScroll: true,
        initialFocus: false,
        // The trigger button is unmounted together with the picker, so the
        // trap cannot return the focus to it. The owning message restores the
        // focus itself when the picker is closed.
        returnFocusOnDeactivate: false
      }}
    >
      <div
        id={menuId}
        {...layerProps}
        role="dialog"
        aria-modal="true"
        aria-hidden={!showReactionPicker}
        tabIndex={-1}
      >
        <EmojiReactions
          emojiData={emojiData}
          reactionClick={handleReactionClick}
          eventId={eventId}
          roomId={roomId}
          // Only the explicitly opened picker may take the focus. The mobile
          // bar follows the focus of the message and must not steal it,
          // otherwise ESC and the arrow keys stop working on the message.
          autoFocus={trapFocus}
        />
      </div>
    </FocusTrap>
  );
  return (
    <div className={classNames.join(' ')}>
      {(showReactionPickerTrigger || showReactionPicker) && (
        <>
          {showTrigger && (
            <Button
              ref={triggerRef}
              variant="tertiary"
              text={intl('matrixChatPortlet.message.react')}
              icon="emoji"
              className="add-reaction"
              data-testid="add-reaction"
              tabIndex={-1}
              onClick={() => setShowReactionPicker(true)}
              aria-expanded={showReactionPicker}
              aria-controls={menuId}
              aria-haspopup="dialog"
            />
          )}
          {showReactionPicker && layer}
        </>
      )}
    </div>
  );
}
